import { createFileRoute } from '@tanstack/react-router';
import { assertPublicToolsEnabled } from '@/lib/public-tools-feature';
import { PdfToAudioDemoPage } from '@/components/astro/new-design-25/SEO/pdf-to-audio-demo-page';
import { hreflangLinks, seo } from '@/seo/seo';

const heroTitle = 'Japanese PDF to audio';
const heroDescription =
  'Drop a Japanese PDF and hear it read aloud with natural Japanese voices. Accurate handling of kanji, hiragana, and katakana, with proper academic terminology and natural Japanese pitch accent.';

const featureFeatures = [
  {
    title: 'Natural Japanese Voices',
    description:
      'Voices trained on real Japanese speech with natural pitch accent and rhythm, not flat phoneme readouts.',
  },
  {
    title: 'Kanji Reading Selection',
    description:
      'Correctly handles common kanji and standard compound words, including on-yomi/kun-yomi distinctions in context.',
  },
  {
    title: 'Academic and Technical Japanese',
    description:
      'Trained on academic, scientific, and professional Japanese, not just conversational speech.',
  },
  {
    title: 'JLPT Learner Friendly',
    description:
      'Slow speeds, synced highlighting in the app, and accurate pronunciation make this useful for N3–N1 listening practice.',
  },
];

const solutionBenefitsData = {
  title: 'Why Japanese readers and learners stay with this PDF tool',
  items: [
    {
      text: 'Natural Japanese voices with proper pitch accent, not a flat low-intonation drone',
      highlight: 'proper pitch accent',
    },
    {
      text: 'Reliable kanji readings for common academic, technical, and everyday compounds',
      highlight: 'kanji readings',
    },
    {
      text: 'Correct handling of mixed kanji, hiragana, and katakana within a single sentence',
      highlight: 'mixed kanji, hiragana, and katakana',
    },
    {
      text: 'Adjustable speed from 0.5x to 4x, ideal for JLPT listening practice and shadowing drills',
      highlight: 'JLPT listening practice',
    },
  ],
};

const useCasesData = {
  title: 'How Japanese PDF to audio gets used',
  description:
    'From students at Japanese universities to JLPT candidates and translators, here is how our Japanese voices are used.',
  useCases: [
    {
      title: 'Student at a Japanese University',
      challenge:
        'Work through Japanese academic reading: textbooks, research PDFs, course handouts in fields from law to engineering.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Drop academic Japanese PDFs and listen with proper readings for technical vocabulary and compound terms.',
        },
        {
          label: 'Generic PDF readers',
          content:
            'Wrong kanji readings on academic words make the audio unreliable, defeating the purpose of listening.',
        },
      ],
    },
    {
      title: 'JLPT N3 to N1 Candidate',
      challenge:
        'Build real listening skill for the JLPT and for using Japanese outside textbook contexts.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Drop any Japanese PDF, slow it down, and shadow with synced text in the Listening app.',
        },
        {
          label: 'Generic PDF readers',
          content:
            'Robotic pacing and pitch trains the wrong ear, then real Japanese audio feels twice as fast as it is.',
        },
      ],
    },
    {
      title: 'Japanese-to-English Translator',
      challenge:
        'Review long Japanese source PDFs efficiently without rereading every kanji from scratch each time.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Listen while reading, with audio handling the readings so eyes can focus on meaning instead of decoding.',
        },
        {
          label: 'Generic PDF readers',
          content:
            'Mispronunciations introduce ambiguity that slows the work instead of speeding it up.',
        },
      ],
    },
    {
      title: 'Heritage Speaker Rebuilding Japanese',
      challenge:
        'Reconnect with Japanese as an adult who grew up speaking it but never fully learned the writing system.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Hear native Japanese audio for any kanji-heavy PDF, with synced highlighting to map sound to character.',
        },
        {
          label: 'Generic PDF readers',
          content:
            'Off-natural pitch accent and wrong kanji readings actively interfere with rebuilding a native ear.',
        },
      ],
    },
  ],
};

const seoSections = [
  {
    title: 'What Japanese PDF Audio Has to Get Right',
    content:
      'Japanese is one of the hardest languages for PDF audio. Three scripts mix together within a single sentence, kanji have multiple readings that depend on context, pitch accent changes meaning, and the rhythm of Japanese is built on morae rather than syllables. Most free Japanese PDF readers get the surface right (sounds in roughly the right order) and the depth completely wrong (pitch accent flat, common compounds mispronounced, technical vocabulary fumbled).',
    contentSecondary:
      'Listening uses Japanese voices trained on real native speech across academic, news, and professional content. The voices read mixed-script Japanese the way a Japanese speaker actually reads it — with the right reading for the context, natural pitch accent, and the cadence that makes Japanese audio comfortable instead of jarring. For learners and serious readers, that is the difference between PDF audio that helps you build a Japanese ear and PDF audio that quietly trains the wrong one.',
  },
  {
    title: 'Japanese PDF to Audio for Study, Translation, and Practice',
    content:
      'For Japanese learners, the bottleneck is rarely vocabulary lists. It is getting enough natural Japanese input at a speed you can follow. Real Japanese podcasts, news, and dramas come at full native speed. Our Japanese PDF tool lets you take any Japanese PDF and convert it into audio at exactly the speed you need, with synced highlighting in the Listening app so you can read along while you build your ear.',
    contentSecondary:
      'For Japanese-language professionals, the same tool turns long Japanese PDFs into ambient input. Translators, journalists, and researchers use the converter on this page to triage Japanese source material and to review final translations against the original. The result opens as a 24-hour read-along link with PDF view, audio, speed controls, and a playbar.',
  },
];

const faqs = [
  {
    question: 'Which Japanese voices are available?',
    answer:
      'Natural Japanese voices in male and female options, trained on real native Japanese speech across academic, news, and professional content.',
  },
  {
    question: 'How are kanji readings handled?',
    answer:
      'Common kanji and standard compound words are read correctly in context, including on-yomi and kun-yomi distinctions. Mixed kanji, hiragana, and katakana within a sentence flow naturally.',
  },
  {
    question: 'Is the pitch accent natural?',
    answer:
      'Yes. Our voices produce natural Japanese pitch accent rather than the flat low-intonation drone common in free Japanese PDF readers.',
  },
  {
    question: 'Can I use this for JLPT listening practice?',
    answer:
      'Yes. Drop any Japanese PDF, slow it to 0.7x, and shadow native pronunciation. Useful from N3 all the way up to N1 preparation.',
  },
];

export const Route = createFileRoute('/_marketing/_seo/japanese-pdf-to-audio')({
  beforeLoad: assertPublicToolsEnabled,
  head: () => ({
    meta: [
      ...seo({
        title:
          'Japanese PDF to Audio — Listen to Japanese PDFs with Native Voices | Listening',
        description:
          'Convert Japanese PDFs to audio with natural Japanese voices. Handles kanji, hiragana, katakana, and academic terminology with proper pitch accent. Free, no sign-up.',
      }),
    ],
    links: [...hreflangLinks('/japanese-pdf-to-audio')],
  }),
  component: () => (
    <PdfToAudioDemoPage
      converterHeading={heroTitle}
      converterSubtitle={heroDescription}
      faqDescription='Quick answers about converting Japanese PDFs to audio.'
      faqTitle='Japanese PDF to audio FAQ'
      faqs={faqs}
      featureBadge='Japanese voices'
      featureFeatures={featureFeatures}
      featureTitle='the Japanese PDF tool that gets the readings right:'
      preferredLang='ja'
      seoSections={seoSections}
      solutionBenefitsData={solutionBenefitsData}
      useCasesData={useCasesData}
    />
  ),
});
